import { createConsumer } from '@rails/actioncable';

// NOTICE: KAPTION-INTEGRATION
// agent-presence.HEARTBEAT.1
// Was 20000, matching the server's former 20s presence TTL. A heartbeat at the
// same cadence had zero margin: a single delayed tick — routine in a
// backgrounded tab, since browsers throttle setTimeout there — lets the
// Redis presence entry expire and the user flips to "offline" until the next
// beat lands. 8s remains comfortably below the current 20s development and
// 90s production defaults in OnlineStatusTracker::PRESENCE_DURATION.
const PRESENCE_INTERVAL = 8000;
const RECONNECT_INTERVAL = 1000;
// NOTICE: KAPTION-INTEGRATION — Increase stale threshold so background tabs
// don't trigger a visible reconnection flash when the user returns. It only
// governs *missed pings*: a socket that actually closes is reopened by
// scheduleReopen (websocket.RECONNECT.1), not after this window.
const STALE_THRESHOLD = 30; // seconds (default is 6)
// NOTICE: KAPTION-INTEGRATION
// websocket.RECONNECT.2
// How long a lost connection may stay down before the "Disconnected" banner
// appears. Every production deploy restarts the servers holding the sockets;
// scheduleReopen brings a socket back within a couple of seconds, so a deploy
// never shows the banner — only a real outage does. Keep this below
// ReconnectService's 15s DISCONNECT_DELAY_THRESHOLD: that margin is what lets
// the catch-up fetch cover a gap whose disconnect is reported this late.
const DISCONNECT_DEBOUNCE = 10000; // ms
// NOTICE: KAPTION-INTEGRATION
// websocket.RECONNECT.1
// @rails/actioncable 6.1 reopens a closed socket only once its
// ConnectionMonitor finds the connection stale, which with STALE_THRESHOLD at
// 30s left every dropped socket down for ~30s (measured: 30.4s). Reopen
// promptly instead, with jitter so a restarted server isn't hit by all of its
// clients at once, backing off while the server stays unreachable.
const FIRST_REOPEN_MIN_DELAY = 500; // ms
const FIRST_REOPEN_MAX_DELAY = 2000; // ms
const MAX_REOPEN_DELAY = 15000; // ms

export function reopenDelay(attempt, random = Math.random()) {
  if (attempt === 0) {
    return (
      FIRST_REOPEN_MIN_DELAY +
      random * (FIRST_REOPEN_MAX_DELAY - FIRST_REOPEN_MIN_DELAY)
    );
  }
  const base = Math.min(MAX_REOPEN_DELAY, 1000 * 2 ** attempt);
  return base * (0.5 + random * 0.5);
}

// NOTICE: KAPTION-INTEGRATION - realtime-auth.SEC.1 (CW-H4): how many times a
// rejected subscription fetches a fresh cable token before giving up.
const MAX_CABLE_TOKEN_REFRESHES = 3;

class BaseActionCableConnector {
  static isDisconnected = false;

  // NOTICE: KAPTION-INTEGRATION - realtime-auth.SEC.1: `cableTokenProvider` is an
  // async function returning a short-lived cable token. With it, the connector
  // subscribes with that token instead of the permanent pubsub_token, and fetches
  // a new one whenever the server rejects the subscription (expired or rotated).
  // Without it, or if fetching fails, it subscribes with the pubsub_token as before.
  constructor(app, pubsubToken, websocketHost = '', cableTokenProvider = null) {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.consumer = createConsumer(websocketURL);

    // Increase tolerance for missed pings (default: 6s = 2 missed pings)
    const { monitor } = this.consumer.connection;
    monitor.constructor.staleThreshold = STALE_THRESHOLD;

    this.disconnectDebounceTimer = null;
    this.reopenTimer = null;
    this.reopenAttempts = 0;
    this.setupVisibilityReconnect();

    // NOTICE: KAPTION-INTEGRATION - realtime-auth.SEC.1
    this.app = app;
    this.pubsubToken = pubsubToken;
    this.cableTokenProvider = cableTokenProvider;
    this.cableTokenRefreshes = 0;
    this.subscription = null;
    if (cableTokenProvider) {
      this.subscribeWithCableToken();
    } else {
      this.subscribe({ pubsub_token: pubsubToken });
    }
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    // NOTICE: KAPTION-INTEGRATION - widget-lazy-realtime.CONNECT.6
    this.kaptionSuspended = false;
    this.isAValidEvent = () => true;
    // NOTICE: KAPTION-INTEGRATION
    // agent-presence.HEARTBEAT.3
    // The heartbeat is a recursive setTimeout chain: whatever schedules the
    // next beat is the previous beat's own callback. One throw from
    // updatePresence used to end the chain for the life of the page — presence
    // then never came back without a reload, with the socket still open and
    // nothing on screen to say so. Guard the call so a single bad beat can
    // never be terminal.
    this.triggerPresenceInterval = () => {
      setTimeout(() => {
        try {
          // NOTICE: KAPTION-INTEGRATION - widget-lazy-realtime.CONNECT.6: no beats while suspended
          if (!this.kaptionSuspended) this.subscription?.updatePresence();
        } catch {
          // A failed beat is survivable; a stopped chain is not.
        }
        this.triggerPresenceInterval();
      }, PRESENCE_INTERVAL);
    };
    this.triggerPresenceInterval();
  }

  // NOTICE: KAPTION-INTEGRATION - realtime-auth.SEC.1
  async subscribeWithCableToken() {
    let cableToken = null;
    try {
      cableToken = await this.cableTokenProvider();
    } catch {
      cableToken = null;
    }
    if (cableToken) {
      this.subscribe({ cable_token: cableToken });
    } else if (this.pubsubToken) {
      this.subscribe({ pubsub_token: this.pubsubToken });
    }
  }

  // NOTICE: KAPTION-INTEGRATION - realtime-auth.SEC.1
  onSubscriptionRejected() {
    if (!this.cableTokenProvider) return;
    if (this.cableTokenRefreshes >= MAX_CABLE_TOKEN_REFRESHES) return;
    this.cableTokenRefreshes += 1;
    this.subscribeWithCableToken();
  }

  subscribe(credentials) {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    const { app } = this;
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        ...credentials,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        // NOTICE: KAPTION-INTEGRATION - realtime-auth.SEC.1
        connected: () => {
          this.cableTokenRefreshes = 0;
          // NOTICE: KAPTION-INTEGRATION - widget-lazy-realtime.CONNECT.5
          this.onKaptionSubscribed();
        },
        rejected: () => this.onSubscriptionRejected(),
        received: this.onReceived,
        // `willAttemptReconnect` is false when the app disconnected on purpose
        // (sign-out) or the server refused reconnection (e.g. unauthorized).
        disconnected: ({ willAttemptReconnect = true } = {}) => {
          // NOTICE: KAPTION-INTEGRATION - widget-lazy-realtime.CONNECT.6: a
          // deliberate suspend is not an outage: no reopen, no banner.
          if (this.kaptionSuspended) return;
          BaseActionCableConnector.isDisconnected = true;
          this.initReconnectTimer();
          if (willAttemptReconnect) this.scheduleReopen();
          // Debounce the UI notification so short reconnections are invisible
          clearTimeout(this.disconnectDebounceTimer);
          this.disconnectDebounceTimer = setTimeout(() => {
            if (BaseActionCableConnector.isDisconnected) {
              this.onDisconnected();
            }
          }, DISCONNECT_DEBOUNCE);
        },
      }
    );
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.clearReopenTimer();
      clearTimeout(this.disconnectDebounceTimer);
      // NOTICE: KAPTION-INTEGRATION
      // agent-presence.HEARTBEAT.4
      // Don't wait up to PRESENCE_INTERVAL to tell the server we are back. The
      // beats sent while the socket was closed were thrown away, so presence
      // may be seconds from expiring at this point.
      this.subscription?.updatePresence();
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // websocket.RECONNECT.1 — see the constants above. Keeps trying until
  // checkConnection sees the socket open again and clears the timer.
  scheduleReopen = () => {
    if (this.reopenTimer) return;
    this.reopenTimer = setTimeout(() => {
      this.reopenTimer = null;
      const { connection } = this.consumer;
      if (connection.isOpen()) return;
      // A socket that is still connecting gets time to finish.
      if (!connection.isActive?.()) {
        this.reopenAttempts += 1;
        connection.reopen();
      }
      this.scheduleReopen();
    }, reopenDelay(this.reopenAttempts));
  };

  clearReopenTimer = () => {
    clearTimeout(this.reopenTimer);
    this.reopenTimer = null;
    this.reopenAttempts = 0;
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // NOTICE: KAPTION-INTEGRATION - widget-lazy-realtime.CONNECT.5
  // eslint-disable-next-line class-methods-use-this
  onKaptionSubscribed = () => {};

  // NOTICE: KAPTION-INTEGRATION - widget-lazy-realtime.CONNECT.6
  // Close the socket on purpose (lazy widget realtime) and keep it closed:
  // the reopen/visibility/presence machinery stays quiet until kaptionResume.
  kaptionSuspend() {
    this.kaptionSuspended = true;
    this.clearReopenTimer();
    this.clearReconnectTimer();
    clearTimeout(this.disconnectDebounceTimer);
    this.consumer.disconnect();
  }

  // Reopen after kaptionSuspend. ActionCable re-sends the existing
  // subscriptions on welcome, so `connected` fires again (CONNECT.5).
  kaptionResume() {
    this.kaptionSuspended = false;
    this.consumer.connect();
    // A socket still closing refuses open(); retry until it is up.
    this.scheduleReopen();
  }

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  setupVisibilityReconnect() {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      // NOTICE: KAPTION-INTEGRATION - widget-lazy-realtime.CONNECT.6
      if (this.kaptionSuspended) return;
      // Proactively reopen on tab return — don't wait for ConnectionMonitor poll
      const { connection } = this.consumer;
      if (!connection.isOpen()) {
        connection.reopen();
      }
      // NOTICE: KAPTION-INTEGRATION
      // agent-presence.HEARTBEAT.2
      // Refresh presence immediately on tab
      // return instead of waiting for the next PRESENCE_INTERVAL tick. A tab
      // backgrounded long enough (Chrome's intensive timer throttling) can
      // lag the heartbeat well past the server's presence TTL even at 8s, so
      // without this a user could still show "offline" for a beat after
      // switching back.
      this.subscription?.updatePresence();
    });
  }

  disconnect() {
    this.clearReopenTimer();
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
